"use client";
import { JobSeekingStatus } from "@nimble/core-client/types";
import type { UserId } from "@nimble/datamodel/types/userId";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "nextjs-toploader/app";
import { toast } from "sonner";

import JobSeekingStatusHeaderView from "@/app/(discover)/components/header/job-seeking-status-header/job-seeking-status-header.view";
import { withAuthGate } from "@/components/shared/auth-paywall/with-auth-gate";
import { getUseApiUserDiscoverInfoQueryKey } from "@/hooks/data/use-api-user-discover-info";
import { useTracking } from "@/hooks/use-tracking";
import coreApiClient from "@/lib/api-clients/core-api-client";
import routes from "@/lib/routes";
import { User } from "@/lib/types/auth";
import { useAuth } from "@/stores/use-auth";

export async function hasAllRequiredFields({
  userId,
  getApiPreferencesByUserId = coreApiClient.getApiPreferencesByUserId,
}: {
  userId: UserId;
  getApiPreferencesByUserId?: typeof coreApiClient.getApiPreferencesByUserId;
}): Promise<boolean> {
  const { data } = await getApiPreferencesByUserId({
    path: {
      user_id: userId,
    },
  });

  if (!data) return false;

  const hasCategories =
    data.categories_connect && data.categories_connect.length > 0;
  const hasGrades = data.grades && data.grades.length > 0;

  return !!(data.location && hasCategories && hasGrades);
}

type UpdatePreferencesResult = { kind: "updated" } | { kind: "redirected" };

export async function updateUserPreferences(
  newStatus: JobSeekingStatus,
  userId: UserId,
  router: ReturnType<typeof useRouter>,
  putApiPreferencesByUserId = coreApiClient.putApiPreferencesByUserId,
  hasAllRequiredFieldsFn = hasAllRequiredFields,
): Promise<UpdatePreferencesResult> {
  const isValid = await hasAllRequiredFieldsFn({ userId: userId });

  if (isValid) {
    await putApiPreferencesByUserId({
      path: {
        user_id: userId,
      },
      body: {
        job_seeking_status: newStatus,
      },
    });

    toast.success("Your job search status has been updated");
    return { kind: "updated" };
  }

  toast.warning(
    "Almost there — complete your profile to unlock status updates",
  );
  router.push(routes.getProfilePreferences());
  return { kind: "redirected" };
}

export function JobSeekingStatusHeaderContainer({ user }: { user: User }) {
  const router = useRouter();
  const { syncAuth } = useAuth();
  const queryClient = useQueryClient();
  const { trackJobSeekingStatusChanged } = useTracking();

  const previousStatus = user.preference?.job_seeking_status ?? null;

  const { isPending, error, mutate, reset } = useMutation({
    mutationFn: async (newStatus: JobSeekingStatus) => {
      const result = await updateUserPreferences(newStatus, user.id, router);
      if (result.kind === "updated") {
        trackJobSeekingStatusChanged({
          previousJobSeekingStatus: previousStatus,
          newJobSeekingStatus: newStatus,
        });
      }
      return result;
    },
    onSuccess: () => {
      syncAuth();
      queryClient.invalidateQueries({
        queryKey: getUseApiUserDiscoverInfoQueryKey(),
      });
    },
  });

  return (
    <JobSeekingStatusHeaderView
      value={previousStatus ?? JobSeekingStatus.CLOSED_TO_WORK}
      onChange={mutate}
      isPending={isPending}
      error={error}
      reset={reset}
    />
  );
}

export default withAuthGate(JobSeekingStatusHeaderContainer, {
  customPaywall: null,
  customLoader: null,
});
